/**
 * Undo / redo for the editor.
 *
 * The history is a chain: the roadmap before the first recorded action
 * (`base`) plus one pure updater per action, in the order the actions were
 * made. The roadmap after the first `n` actions is `base` folded through the
 * first `n` updaters, so undoing entry `n` restores the fold of `n - 1` and
 * redoing it restores the fold of `n` (see `stateAround`). Recording updaters
 * instead of snapshots keeps entries right when actions overlap: an item
 * created while another request is still pending gets its updater only when
 * the server answers, and every later entry folds on top of it.
 *
 * Lives in memory for the open editor only. `useUndoRedo` in queries.ts does
 * the restoring.
 */
import type { RoadmapDetail } from "@shared/types";
import { create } from "zustand";

export type Updater = (detail: RoadmapDetail) => RoadmapDetail;

export interface HistoryEntry {
  id: string;
  /** Human label, e.g. "Move “Onboarding v2”". */
  label: string;
  /** How the action changes the roadmap; null until the server has answered. */
  update: Updater | null;
}

const MAX_ENTRIES = 100;

interface HistoryState {
  roadmapId: string | null;
  /** The roadmap before the first entry. */
  base: RoadmapDetail | null;
  entries: HistoryEntry[];
  /** Entries before the cursor are applied; the ones after it can be redone. */
  cursor: number;
  /** Record an action (drops the redo entries). A different roadmap starts fresh. */
  push: (roadmapId: string, before: RoadmapDetail, entry: HistoryEntry) => void;
  /** Give a pending entry its updater once the server has answered. */
  settle: (id: string, update: Updater) => void;
  /** Forget an entry whose mutation failed (the cache was rolled back already). */
  remove: (id: string) => void;
  /** Move the cursor back / forward by one and return the entry crossed. */
  stepBack: () => HistoryEntry | undefined;
  stepForward: () => HistoryEntry | undefined;
  clear: () => void;
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
  roadmapId: null,
  base: null,
  entries: [],
  cursor: 0,
  push: (roadmapId, before, entry) =>
    set((s) => {
      const fresh = s.roadmapId !== roadmapId || !s.base;
      const kept = fresh ? [] : s.entries.slice(0, s.cursor);
      let base = fresh ? before : s.base;
      let entries = [...kept, entry];
      if (entries.length > MAX_ENTRIES && base) {
        // Fold the oldest entries into the base so the chain stays short.
        const drop = entries.length - MAX_ENTRIES;
        const oldest = entries.slice(0, drop);
        if (oldest.every((e) => e.update)) {
          base = oldest.reduce((d, e) => e.update!(d), base);
          entries = entries.slice(drop);
        }
      }
      return { roadmapId, base, entries, cursor: entries.length };
    }),
  settle: (id, update) =>
    set((s) => ({
      entries: s.entries.map((e) => (e.id === id && !e.update ? { ...e, update } : e)),
    })),
  remove: (id) =>
    set((s) => {
      const index = s.entries.findIndex((e) => e.id === id);
      if (index === -1) return s;
      return {
        entries: s.entries.filter((e) => e.id !== id),
        cursor: index < s.cursor ? s.cursor - 1 : s.cursor,
      };
    }),
  stepBack: () => {
    const { entries, cursor } = get();
    if (cursor === 0) return undefined;
    set({ cursor: cursor - 1 });
    return entries[cursor - 1];
  },
  stepForward: () => {
    const { entries, cursor } = get();
    if (cursor >= entries.length) return undefined;
    set({ cursor: cursor + 1 });
    return entries[cursor];
  },
  clear: () => set({ roadmapId: null, base: null, entries: [], cursor: 0 }),
}));

/** Fold the base through the first `n` entries; null while one of them is still pending. */
export function stateAt(
  s: Pick<HistoryState, "base" | "entries">,
  n: number,
): RoadmapDetail | null {
  if (!s.base) return null;
  let detail = s.base;
  for (const entry of s.entries.slice(0, n)) {
    if (!entry.update) return null;
    detail = entry.update(detail);
  }
  return detail;
}

/**
 * The roadmap just before (undo) or just after (redo) an entry, once every
 * entry up to there has been settled by the server. Null when the entry is
 * gone (its mutation failed) or the history was cleared meanwhile.
 */
export function stateAround(
  roadmapId: string,
  entryId: string,
  side: "before" | "after",
): Promise<RoadmapDetail | null> {
  return new Promise((resolve) => {
    const attempt = (): boolean => {
      const s = useHistoryStore.getState();
      if (s.roadmapId !== roadmapId || !s.base) {
        resolve(null);
        return true;
      }
      const index = s.entries.findIndex((e) => e.id === entryId);
      if (index === -1) {
        resolve(null);
        return true;
      }
      const detail = stateAt(s, side === "before" ? index : index + 1);
      if (!detail) return false; // a request is still pending: try again on the next change
      resolve(detail);
      return true;
    };
    if (attempt()) return;
    const unsubscribe = useHistoryStore.subscribe(() => {
      if (attempt()) unsubscribe();
    });
  });
}
